import { Typography } from '@euler/app/components/typography';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { Colors, StatusColors } from '@euler/components/Colors';
import { getDefectiveLevelColor } from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useVehicleSystemModules } from '@euler/functions/useVehicleSystemModules';
import {
  DefectiveLevel,
  DefectiveStats,
  getSumOfDefectiveSummary,
} from '@euler/model';
import { VehicleSystemModule } from '@euler/model/entity';
import {
  ReactNode,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  LayoutChangeEvent,
  LayoutRectangle,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import Animated, {
  Easing,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useTheme } from 'styled-components';

export type InspectionBottomTabType = 'overview' | 'module' | 'others';

export type InspectionBottomTabInfo =
  | {
      type: 'overview' | 'others';
    }
  | {
      type: 'module';
      mod: VehicleSystemModule;
    };

export type SystemModuleStats = Map<
  number,
  {
    defectiveStats: DefectiveStats;
    defectiveLevel: DefectiveLevel;
  }
>;

export const WiredInspectionBottomTabs = memo(
  ({
    style,
    activeTab,
    moduleStats,
    onSystemModulesLoaded,
    onTabPress,
  }: {
    style?: StyleProp<ViewStyle>;
    activeTab: InspectionBottomTabInfo;
    moduleStats?: SystemModuleStats;
    onSystemModulesLoaded?: (modules: VehicleSystemModule[]) => void;
    onTabPress?: (tab: InspectionBottomTabInfo) => void;
  }) => {
    const globalTheme = useTheme();
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const maxTranslateY = rpx(148);
    const translateY = useSharedValue(maxTranslateY);
    const animatedStyle = useAnimatedStyle(() => ({
      opacity: interpolate(translateY.value, [0, maxTranslateY], [1, 0]),
      transform: [{ translateY: translateY.value }],
    }));

    const onOverviewTabPress = useCallback(() => {
      onTabPress?.({ type: 'overview' });
    }, [onTabPress]);

    const onOthersTabPress = useCallback(() => {
      onTabPress?.({ type: 'others' });
    }, [onTabPress]);

    const onModuleTabPress = useCallback(
      (mod: VehicleSystemModule) => {
        onTabPress?.({ type: 'module', mod });
      },
      [onTabPress],
    );

    const otherStats = moduleStats?.get(0);

    const showOtherRedDot =
      otherStats != null &&
      getSumOfDefectiveSummary(otherStats.defectiveStats) > 0;

    const otherRedDotColor =
      otherStats != null
        ? getDefectiveLevelColor(globalTheme, otherStats.defectiveLevel)
        : undefined;

    const systemModules = useVehicleSystemModules();

    useEffect(() => {
      if (systemModules != null) {
        onSystemModulesLoaded?.(systemModules);
        translateY.value = withTiming(0, {
          duration: 350,
          easing: Easing.inOut(Easing.linear),
        });
      }
    }, [onSystemModulesLoaded, systemModules, translateY]);

    return (
      <Animated.View
        css={`
          flex-direction: row;
          align-items: stretch;
          justify-content: space-between;
          background-color: ${theme.task.bottomBar.bg};
          border-top-left-radius: ${rpx(40)}px;
          border-top-right-radius: ${rpx(40)}px;
        `}
        style={[style, animatedStyle]}
      >
        <TabCell
          text="综合"
          active={activeTab.type === 'overview'}
          onPress={onOverviewTabPress}
        />
        <Suspense fallback={null}>
          <ModuleTabs
            moduleStats={moduleStats}
            systemModules={systemModules}
            activeModId={
              activeTab.type === 'module' ? activeTab.mod.id : undefined
            }
            onPress={onModuleTabPress}
          />
        </Suspense>
        {otherStats != null ? (
          <TabCell
            text="其他"
            active={activeTab.type === 'others'}
            onPress={onOthersTabPress}
            redDot={showOtherRedDot}
            redDotStyle={
              otherRedDotColor
                ? { backgroundColor: otherRedDotColor }
                : undefined
            }
          />
        ) : null}
      </Animated.View>
    );
  },
);

const ModuleTabs = memo(
  ({
    activeModId,
    moduleStats,
    systemModules,
    onPress,
  }: {
    activeModId?: number;
    moduleStats?: SystemModuleStats;
    systemModules: VehicleSystemModule[] | null | undefined;
    onPress?: (mod: VehicleSystemModule) => void;
  }) => {
    if (systemModules == null) {
      return null;
    }
    return (
      <>
        {systemModules.map(mod => (
          <ModuleTabCell
            key={mod.id}
            mod={mod}
            active={mod.id === activeModId}
            defectiveStats={moduleStats?.get(mod.id)?.defectiveStats}
            defectiveLevel={moduleStats?.get(mod.id)?.defectiveLevel}
            onPress={onPress}
          />
        ))}
      </>
    );
  },
);

const ModuleTabCell = memo(
  ({
    mod,
    active,
    defectiveStats,
    defectiveLevel,
    onPress,
  }: {
    mod: VehicleSystemModule;
    active?: boolean;
    defectiveStats?: DefectiveStats;
    defectiveLevel?: DefectiveLevel;
    onPress?: (mod: VehicleSystemModule) => void;
  }) => {
    const theme = useTheme();
    const total =
      defectiveStats != null ? getSumOfDefectiveSummary(defectiveStats) : 0;
    const color =
      defectiveLevel != null
        ? getDefectiveLevelColor(theme, defectiveLevel)
        : undefined;
    const handlePress = useCallback(() => {
      onPress?.(mod);
    }, [mod, onPress]);
    return (
      <TabCell
        text={mod.name}
        onPress={handlePress}
        active={active}
        redDot={total > 0}
        redDotStyle={color != null ? { backgroundColor: color } : undefined}
      />
    );
  },
);

const kRedDotSize = 10;
const kRedDotPadding = 0;

const TabCell = memo(
  ({
    text,
    active,
    style,
    redDot,
    redDotStyle,
    children,
    onPress,
  }: {
    text: string;
    active?: boolean;
    style?: StyleProp<ViewStyle>;
    children?: ReactNode;
    redDot?: boolean;
    redDotStyle?: StyleProp<ViewStyle>;
    onPress?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();
    const [labelLayout, setLabelLayout] = useState<LayoutRectangle>();

    const redDotSize = rpx(kRedDotSize);
    const redDotPadding = rpx(kRedDotPadding);

    const onLabelLayout = useCallback((e: LayoutChangeEvent) => {
      setLabelLayout(e.nativeEvent.layout);
    }, []);

    return (
      <TouchableOpacity
        css={`
          flex: 1;
          justify-content: center;
          align-items: center;
        `}
        style={style}
        onPress={onPress}
      >
        <Typography.Label
          medium
          color={
            active
              ? theme.task.bottomBar.activeTextColor
              : theme.task.bottomBar.textColor
          }
          size={rpx(32)}
          onLayout={onLabelLayout}
        >
          {text}
        </Typography.Label>
        {redDot && labelLayout != null ? (
          <View
            css={`
              position: absolute;
              left: ${labelLayout.x + labelLayout.width + rpx(2)}px;
              top: ${labelLayout.y}px;
              width: ${redDotSize}px;
              height: ${redDotSize}px;
              border-radius: ${redDotSize / 2}px;
              background-color: ${Colors.White0_5};
              padding: ${redDotPadding}px;
            `}
          >
            <View
              css={`
                background-color: ${StatusColors.Danger};
                width: 100%;
                height: 100%;
                border-radius: ${(redDotSize - redDotPadding * 2) / 2}px;
              `}
              style={redDotStyle}
            />
          </View>
        ) : null}
        {children}
      </TouchableOpacity>
    );
  },
);
